//#region IMPORT
import React, { useState, useEffect } from 'react';
import M_WOS_BASIC_01 from './M_WOS_BASIC_01';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { NavBarInfoActions } from '../../../Reducer/NavBarInfoReducer';
import { BottomSheetActions } from '../../../Reducer/BottomSheetReducer';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import { WorkingCheckActions } from '../../../Reducer/WorkingCheckReducer';
import useFcmHandler from '../../../Hooks/useFcmHandler';
import {
  useSearch3,
  useSearch6,
  useSave1,
  useSave2,
  useSearch5,
} from './M_WOS_BASIC_01_Query';
import { useSave } from '../../../Hooks/useSave';
import useFileUpload from '../../../Hooks/useFileUpload';
import { WOS_BASIC_01_Schema } from './M_WOS_BASIC_01_Schema';
import { useAppNavigate } from '../../../Hooks/back/useAppNavigate';
import { useBackStack } from '../../../Hooks/back/useBackStack';
import Global from '../../../Services/Common/Global';
//#endregion

const INPUTS = {
  workStopCd: '',
  depCd: '',
  stopAuth: '',
  stopComment: '',
  stopImagePath: '',
  userId: '',
};

export default function M_WOS_BASIC_01F() {
  const { navigate } = useAppNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const [workStopInputs, setWorkStopInputs] = useState(INPUTS);
  const [errors, setErrors] = useState({});
  const [selectedTargets, setSelectedTargets] = useState([]);
  const sendFcm = useFcmHandler();
  const bottomSheetState = useSelector((state) => state.bottomSheet);
  const [masterData, setMasterData] = useState([]);
  const workStopUpload = useFileUpload({
    oldFilePathField: 'image',
    newFileNameField: 'saveName',
    maxCount: 5,
    getTargetPath: () => `WorkStop/${userInfo.custCd}/WOS_BASIC_01`,
  });

  // 작업 중지 지시 등: 상세에서 넘어온 데이터로 사진·내용 미리 채움
  useEffect(() => {
    const data = location.state?.data;
    if (!data) return;

    setWorkStopInputs((prev) => ({
      ...prev,
      workStopCd: data.workStopCd ?? '',
      depCd: data.depCd ?? '',
      stopAuth: data.stopAuth ?? '',
      stopComment: data.stopComment ?? '',
      stopImagePath: data.stopImagePath ?? '',
    }));

    const paths = data.stopImagePath
      ? data.stopImagePath
          .split(',')
          .map((p) => p.trim())
          .filter(Boolean)
      : [];

    if (paths.length > 0) {
      workStopUpload.setPreviewList(
        paths.map((path) => {
          const fileName = path.split('/').pop() || 'image';
          return {
            preview: `${Global.file.useUrl}files/${path}`,
            image: path,
            saveName: fileName,
            fileName,
            fileType: '',
          };
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.data?.workStopCd]);

  // 스택형 뒤로가기로 변경: 작업 중지 작성 이탈 시 확인 시트 표시
  useBackStack({
    type: 'sheet',
    enabled: !bottomSheetState.bottom,
    onBack: () =>
      dispatch(BottomSheetActions.OpenBottomSheet({ type: 'backClickWos' })),
  });

  useEffect(() => {
    Init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const Init = () => {
    dispatch(NavBarInfoActions.setType('titleBar'));
    dispatch(NavBarInfoActions.setTitleName('작업 중지'));
    // Search1Async({ userInfo });
  };

  //#region 쿼리
  const { runAsync: Search3Async } = useSearch3({
    onSuccess: (rows) => {
      if (rows?.[0]?.type === 'Error') {
        dispatch(
          showSnackbar({
            message: '인원 조회 중 오류가 발생했습니다.',
            type: 'error',
          })
        );
      }
    },
  });
  const { refetch: Search5 } = useSearch5({
    onSuccess: (rows) => {
      const processed = (rows || []).map((item) => {
        const path = [
          item.depNm1,
          item.depNm2,
          item.depNm3,
          item.depNm4,
        ].filter(Boolean);
        return {
          ...item,
          pathArray: path,
          depth: path.length,
        };
      });
      setMasterData(processed);
    },
  });
  const { runAsync: Search6Async } = useSearch6();
  const { runAsync: Save1Async } = useSave1();
  const { runAsync: Save2Async } = useSave2();

  const getStopAuth = () => {
    if (userInfo.authCd === 'HEAD_MANAGER') return '1';
    if (userInfo.authCd === 'BUSINESS_OWNER') return '2';
    return '0';
  };

  // 작업중지 테이블 저장
  const { run: Save1 } = useSave({
    menuId: 'M_WOS_BASIC_01',
    action: 'SAVE_WORK_STOP',
    schema: WOS_BASIC_01_Schema,
    saveData: () => ({
      workStopCd: workStopInputs.workStopCd ?? '',
      depCd: userInfo.depCd,
      stopAuth: getStopAuth(),
      stopComment: workStopInputs.stopComment ?? '',
      stopImagePath: '',
    }),
    requestSave: async (rows) => {
      const row = rows[0] ?? {};
      const res = await Save1Async({
        row: { ...row, stopImagePath: '' },
        userInfo,
      });
      const workStopCd = res?.[0]?.workStopCd ?? res?.workStopCd;
      if (!workStopCd) return res;

      setWorkStopInputs((prev) => ({ ...prev, workStopCd }));

      let stopImagePath = '';
      const validImages = workStopUpload.previewList.filter(
        (img) => !img.deleted
      );
      if (validImages.length > 0) {
        const existingPaths = validImages
          .filter((img) => img.image && !img.previewFilePath)
          .map((img) => img.image);
        const uploadedPaths =
          (await workStopUpload.SaveLogicFile(
            validImages.filter((img) => img.previewFilePath)
          )) || '';
        const combined = [
          ...existingPaths,
          ...(uploadedPaths
            ? uploadedPaths.split(',').map((p) => p.trim())
            : []),
        ].filter(Boolean);
        stopImagePath = combined.join(',');
        await Save1Async({
          row: {
            ...row,
            workStopCd,
            stopImagePath,
          },
          userInfo,
        });
      }

      await SaveLogic2(workStopCd, getStopAuth());
      dispatch(WorkingCheckActions.setStopWorking(1));
      dispatch(WorkingCheckActions.setWorkStopCd(workStopCd));
      if (
        userInfo.authCd === 'BUSINESS_OWNER' ||
        userInfo.authCd === 'HEAD_MANAGER' ||
        (userInfo.authCd === 'EMPLOYEE' && userInfo.posiNm === '관리감독자')
      ) {
        navigate('/Home');
      } else {
        navigate(`/M_WorkStop/${workStopCd}`);
      }
      return res;
    },
  });
  //#endregion

  // 작업중지 멤버 저장
  const SaveLogic2 = async (workStopCd, stopAuth) => {
    try {
      // 근로자(0)인 경우(관리자 제외)
      if (stopAuth === '0') {
        if (userInfo?.decUserId) {
          await Save2Async({
            row: { workStopCd, stopId: userInfo?.decUserId },
            userInfo,
          });
        }

        // 비상연락망
        const emTel = await Search6Async();
        const emArr = Array.isArray(emTel)
          ? emTel
          : (emTel?.data ?? emTel?.Data ?? []);

        for (const emT of emArr) {
          if (!emT.manageId) continue;
          await sendFcm({
            targetUserId: emT.manageId,
            requestUserId: userInfo.userid,
            body: `${userInfo.userNm}님이 작업 중지를 요청했습니다.`,
            status: '작업중지',
            codeGroup: 'AL_006_001',
            title: '작업중지',
            route: '/M_WorkStop',
            responseData: workStopCd,
          });
        }
        return;
      }

      // 관리자인 경우
      if (selectedTargets.length > 0) {
        // 부서 인원(근로자)
        const members = await Search3Async({
          depCdList: selectedTargets.map((t) => t.depCd),
        });
        const memberList = Array.isArray(members)
          ? members
          : (members?.data ?? members?.Data ?? []);

        for (const member of memberList) {
          if (!member.id) continue;

          await Save2Async({
            row: { workStopCd, stopId: member.id },
            userInfo,
          });

          await sendFcm({
            targetUserId: member.id,
            requestUserId: userInfo.userid,
            body: `${userInfo.userNm}님이 작업 중지를 요청했습니다.`,
            status: '작업중지',
            codeGroup: 'AL_006_001',
            title: '작업중지',
            route: '/M_WorkStop',
            responseData: workStopCd,
          });
        }

        // 비상연락망
        const emTel = await Search6Async();
        const emArr = Array.isArray(emTel)
          ? emTel
          : (emTel?.data ?? emTel?.Data ?? []);

        for (const emT of emArr) {
          if (!emT.manageId) continue;
          await sendFcm({
            targetUserId: emT.manageId,
            requestUserId: userInfo.userid,
            body: `${userInfo.userNm}님이 작업 중지를 요청했습니다.`,
            status: '작업중지',
            codeGroup: 'AL_006_001',
            title: '작업중지',
            route: '/M_WorkStop',
            responseData: workStopCd,
          });
        }
        return;
      }
    } catch (e) {
      console.error('SaveLogic2 Error:', e);
    }
  };

  return (
    <div className="wrap">
      <M_WOS_BASIC_01
        location={location}
        workStopUpload={workStopUpload}
        selectedTargets={selectedTargets}
        setSelectedTargets={setSelectedTargets}
        errors={errors}
        setErrors={setErrors}
        workStopInputs={workStopInputs}
        setWorkStopInputs={setWorkStopInputs}
        SaveLogic1={Save1}
        masterData={masterData}
      />
    </div>
  );
}
